import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Heart,
  Clock,
  Users,
  DollarSign,
  Leaf,
  Dumbbell,
  Baby,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Utensils,
  Timer,
  Home,
  ArrowRight
} from 'lucide-react';
import { haptic } from '@/utils/haptics';
import { trackPlanStarted, trackPathUsed } from '@/utils/analytics';

// Goal options for pre-signup teaser
const GOALS = [
  { id: 'healthy', label: 'Eat Healthier', icon: Heart, color: 'text-green-500', description: 'Nutritious meals that taste great' },
  { id: 'time', label: 'Save Time', icon: Clock, color: 'text-blue-500', description: 'Quick recipes under 30 min' },
  { id: 'family', label: 'Feed My Family', icon: Users, color: 'text-purple-500', description: 'Kid-friendly meals everyone loves' },
  { id: 'budget', label: 'Eat on a Budget', icon: DollarSign, color: 'text-amber-500', description: 'Delicious meals that save money' },
];

// Household size options
const HOUSEHOLD_SIZES = [
  { value: 1, label: 'Just me', icon: '👤' },
  { value: 2, label: 'Couple', icon: '👥' },
  { value: 3, label: 'Small Family', icon: '👨‍👩‍👧', sublabel: '3-4 people' },
  { value: 4, label: 'Large Family', icon: '👨‍👩‍👧‍👦', sublabel: '5+ people' },
];

// Time preferences
const TIME_PREFERENCES = [
  { value: 15, label: 'Super Quick', sublabel: 'Under 15 min', icon: '⚡' },
  { value: 30, label: 'Quick & Easy', sublabel: '15-30 min', icon: '🕐' },
  { value: 45, label: 'Worth the wait', sublabel: '30-45 min', icon: '👨‍🍳' },
  { value: 60, label: 'I love to cook', sublabel: '45+ min', icon: '❤️' },
];

// Dietary constraints
const DIETARY_OPTIONS = [
  { id: 'none', label: 'No Restrictions', icon: Utensils },
  { id: 'vegetarian', label: 'Vegetarian', icon: Leaf },
  { id: 'vegan', label: 'Vegan', icon: Leaf },
  { id: 'gluten_free', label: 'Gluten-Free', icon: Sparkles },
  { id: 'dairy_free', label: 'Dairy-Free', icon: Sparkles },
  { id: 'keto', label: 'Keto/Low Carb', icon: Dumbbell },
];

// Path recommendations based on preferences
const PATH_RECOMMENDATIONS = {
  healthy: 'weeknight-healthy',
  time: 'low-prep',
  family: 'family-friendly',
  budget: 'budget-friendly',
  default: 'weeknight-healthy',
};

// Path details for recommendation display
const PATH_DETAILS = {
  'weeknight-healthy': {
    name: 'Weeknight Healthy',
    description: 'Balanced meals in 30 min or less',
    icon: Heart,
    color: 'bg-green-500',
    time: '30 min avg',
    meals: 5
  },
  'family-friendly': {
    name: 'Family Friendly',
    description: 'Kid-approved meals everyone will love',
    icon: Users,
    color: 'bg-purple-500',
    time: '35 min avg',
    meals: 5
  },
  'low-prep': {
    name: 'Low Prep',
    description: 'Minimal cooking, maximum flavor',
    icon: Timer,
    color: 'bg-blue-500',
    time: '20 min avg',
    meals: 5
  },
  'budget-friendly': {
    name: 'Budget Friendly',
    description: 'Delicious meals that save money',
    icon: DollarSign,
    color: 'bg-amber-500',
    time: '30 min avg',
    meals: 5
  },
  'high-protein': {
    name: 'High Protein',
    description: 'Fuel your fitness goals',
    icon: Dumbbell,
    color: 'bg-red-500',
    time: '35 min avg',
    meals: 5
  },
};

/**
 * Pre-Signup Teaser - Light 2-3 tap flow on landing page
 */
export const PreSignupTeaser = ({ onComplete, onSignup }) => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const handleGoalSelect = (goalId) => {
    setSelectedGoal(goalId);
    haptic('light');
    
    // Brief delay then show result
    setTimeout(() => {
      setShowResult(true);
      haptic('medium');
    }, 300);
  };

  const recommendedPathId = selectedGoal ? PATH_RECOMMENDATIONS[selectedGoal] : null;
  const recommendedPath = recommendedPathId ? PATH_DETAILS[recommendedPathId] : null;

  if (showResult && recommendedPath) {
    return (
      <div className="animate-fade-in" data-testid="teaser-result">
        <div className="text-center mb-6">
          <Badge variant="secondary" className="mb-3">Perfect Match</Badge>
          <h3 className="text-2xl font-semibold mb-2">We recommend</h3>
        </div>
        
        <Card className={`border-2 border-primary/50 bg-primary/5 mb-6`}>
          <CardContent className="p-6 text-center">
            <div className={`w-16 h-16 rounded-full ${recommendedPath.color} mx-auto mb-4 flex items-center justify-center`}>
              <recommendedPath.icon className="h-8 w-8 text-white" />
            </div>
            <h4 className="text-xl font-semibold mb-1">{recommendedPath.name}</h4>
            <p className="text-muted-foreground mb-3">{recommendedPath.description}</p>
            <div className="flex justify-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {recommendedPath.time}
              </span>
              <span className="flex items-center gap-1">
                <Utensils className="h-4 w-4" />
                {recommendedPath.meals} dinners/week
              </span>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <Button 
            size="lg" 
            className="w-full rounded-full h-14 text-lg"
            onClick={() => onSignup(selectedGoal, recommendedPathId)}
            data-testid="teaser-get-plan-btn"
          >
            Get My Plan Free
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
          <Button 
            variant="ghost" 
            className="w-full"
            onClick={() => {
              setShowResult(false);
              setSelectedGoal(null);
            }}
          >
            Choose Different Goal
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pre-signup-teaser">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-semibold mb-2">What&apos;s your goal?</h3>
        <p className="text-muted-foreground">We&apos;ll build your perfect meal week</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {GOALS.map((goal) => (
          <Card 
            key={goal.id}
            className={`cursor-pointer transition-all hover:border-primary/50 hover:shadow-md ${
              selectedGoal === goal.id ? 'border-primary ring-2 ring-primary/20' : ''
            }`}
            onClick={() => handleGoalSelect(goal.id)}
            data-testid={`goal-${goal.id}`}
          >
            <CardContent className="p-4 text-center">
              <div className={`w-12 h-12 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center`}>
                <goal.icon className={`h-6 w-6 ${goal.color}`} />
              </div>
              <p className="font-medium text-sm">{goal.label}</p>
              <p className="text-xs text-muted-foreground mt-1">{goal.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

/**
 * Post-Signup Full Onboarding Modal
 */
export const OnboardingModal = ({ open, onOpenChange, onComplete }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  
  // Preferences
  const [householdSize, setHouseholdSize] = useState(null);
  const [timePreference, setTimePreference] = useState(null);
  const [dietary, setDietary] = useState([]);
  const [recommendedPath, setRecommendedPath] = useState(null);

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    haptic('light');
    if (step < 3) {
      setStep(step + 1);
    } else if (step === 3) {
      // Calculate recommended path
      calculateRecommendation();
      setStep(4);
    }
  };

  const handleBack = () => {
    haptic('light');
    if (step > 1) setStep(step - 1);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return householdSize !== null;
      case 2: return timePreference !== null;
      case 3: return true; // Dietary is optional
      default: return true;
    }
  };

  const calculateRecommendation = () => {
    // Simple recommendation logic based on preferences
    let pathId = 'weeknight-healthy';
    
    if (householdSize >= 3) {
      pathId = 'family-friendly';
    } else if (timePreference <= 20) {
      pathId = 'low-prep';
    } else if (dietary.includes('keto')) {
      pathId = 'high-protein';
    }
    
    setRecommendedPath(PATH_DETAILS[pathId]);
    setRecommendedPath({ ...PATH_DETAILS[pathId], id: pathId });
  };

  const handleGeneratePlan = async () => {
    if (!recommendedPath) return;
    
    setGenerating(true);
    haptic('medium');
    
    // Track analytics
    trackPlanStarted(recommendedPath.id, recommendedPath.name);
    trackPathUsed(recommendedPath.id, recommendedPath.name);
    
    try {
      // Save preferences
      await api.put('/user/preferences', {
        household_size: householdSize,
        time_preference: timePreference,
        dietary_restrictions: dietary,
        onboarding_completed: true,
        recommended_path: recommendedPath.id
      });
      
      // Generate the meal plan
      const response = await api.post(`/paths/${recommendedPath.id}/generate`, {
        path_id: recommendedPath.id,
        meals_count: 5
      });
      
      haptic('success');
      toast.success(`${response.data.meals_generated} meals added to your week!`);
      
      onOpenChange(false);
      if (onComplete) onComplete();
      navigate('/meal-planner');
    } catch (error) {
      console.error('Failed to generate plan:', error);
      toast.error('Failed to generate plan. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleChooseDifferent = () => {
    onOpenChange(false);
    navigate('/dashboard');
  };

  const toggleDietary = (id) => {
    haptic('light');
    if (id === 'none') {
      setDietary([]);
    } else {
      setDietary(prev => 
        prev.includes(id) 
          ? prev.filter(d => d !== id)
          : [...prev.filter(d => d !== 'none'), id]
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="text-center">
            {step < 4 ? "Let's set up your week" : "Your Perfect Plan"}
          </DialogTitle>
          {step < 4 && (
            <DialogDescription className="text-center">
              Step {step} of {totalSteps - 1}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Progress Bar */}
        <Progress value={progress} className="h-1 mb-6" />

        {/* Step 1: Household Size */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-household">
            <h3 className="text-lg font-medium text-center">How many people are you cooking for?</h3>
            <p className="text-sm text-muted-foreground text-center">This helps us size recipes and grocery quantities.</p>
            <div className="grid grid-cols-2 gap-3">
              {HOUSEHOLD_SIZES.map((size) => (
                <Card
                  key={size.value}
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    householdSize === size.value ? 'border-primary ring-2 ring-primary/20' : ''
                  }`}
                  onClick={() => { setHouseholdSize(size.value); haptic('light'); }}
                  data-testid={`household-${size.value}`}
                >
                  <CardContent className="p-4 text-center">
                    <span className="text-3xl mb-2 block">{size.icon}</span>
                    <p className="font-medium">{size.label}</p>
                    {size.sublabel && (
                      <p className="text-xs text-muted-foreground">{size.sublabel}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Time Preference */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-time">
            <h3 className="text-lg font-medium text-center">How much time do you have to cook?</h3>
            <p className="text-sm text-muted-foreground text-center">We&apos;ll prioritize meals that fit your schedule.</p>
            <div className="grid grid-cols-2 gap-3">
              {TIME_PREFERENCES.map((pref) => (
                <Card
                  key={pref.value}
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    timePreference === pref.value ? 'border-primary ring-2 ring-primary/20' : ''
                  }`}
                  onClick={() => { setTimePreference(pref.value); haptic('light'); }}
                  data-testid={`time-${pref.value}`}
                >
                  <CardContent className="p-4 text-center">
                    <span className="text-2xl mb-2 block">{pref.icon}</span>
                    <p className="font-medium text-sm">{pref.label}</p>
                    <p className="text-xs text-muted-foreground">{pref.sublabel}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Dietary */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-dietary">
            <h3 className="text-lg font-medium text-center">Any dietary preferences?</h3>
            <p className="text-sm text-muted-foreground text-center">Select all that apply (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              {DIETARY_OPTIONS.map((option) => (
                <Card
                  key={option.id}
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    (option.id === 'none' && dietary.length === 0) || dietary.includes(option.id)
                      ? 'border-primary ring-2 ring-primary/20' 
                      : ''
                  }`}
                  onClick={() => toggleDietary(option.id)}
                  data-testid={`dietary-${option.id}`}
                >
                  <CardContent className="p-3 text-center">
                    <option.icon className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="font-medium text-sm">{option.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-xs text-muted-foreground text-center italic">We&apos;ll default to flexible, familiar meals.</p>
          </div>
        )}

        {/* Step 4: Recommendation - The "Relief Moment" */}
        {step === 4 && recommendedPath && (
          <div className="space-y-6" data-testid="step-recommendation">
            {/* Confident header - no hesitation */}
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-1">Based on what you told us, we recommend:</p>
              <h3 className="text-2xl font-semibold">{recommendedPath.name}</h3>
              <p className="text-primary font-medium">{recommendedPath.time} cook time</p>
            </div>
            
            {/* Path Card - Visual confirmation */}
            <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full ${recommendedPath.color} flex-shrink-0 flex items-center justify-center`}>
                    <recommendedPath.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-muted-foreground text-sm mb-1">{recommendedPath.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Utensils className="h-4 w-4" />
                        {recommendedPath.meals} dinners/week
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {recommendedPath.time}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* What you'll get - builds confidence */}
                <div className="mt-4 pt-4 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2">What you&apos;ll get:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" />
                      <span>Ingredient overlap optimization</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" />
                      <span>Less food waste</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" />
                      <span>Variety across the week</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-primary" />
                      <span>One-click grocery list</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA - Big, confident, no hesitation */}
            <div className="space-y-2">
              <Button 
                size="lg" 
                className="w-full rounded-full h-14 text-lg font-medium"
                onClick={handleGeneratePlan}
                disabled={generating}
                data-testid="generate-plan-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Building your week...
                  </>
                ) : (
                  <>
                    Create My Week
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">You can change this anytime.</p>
              <Button 
                variant="ghost" 
                className="w-full text-muted-foreground"
                onClick={handleChooseDifferent}
                disabled={generating}
              >
                Choose a different path
              </Button>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        {step < 4 && (
          <div className="flex justify-between mt-6">
            <Button 
              variant="ghost" 
              onClick={handleBack}
              disabled={step === 1}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button 
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="next-step-btn"
            >
              {step === 3 ? 'See My Recommendation' : 'Next'}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Path Cards Component - For displaying on landing page below the fold
 */
export const PathCards = ({ onSelect, compact = false }) => {
  const paths = [
    { id: 'weeknight-healthy', ...PATH_DETAILS['weeknight-healthy'] },
    { id: 'family-friendly', ...PATH_DETAILS['family-friendly'] },
    { id: 'high-protein', ...PATH_DETAILS['high-protein'] },
    { id: 'low-prep', ...PATH_DETAILS['low-prep'] },
    { id: 'budget-friendly', ...PATH_DETAILS['budget-friendly'] },
  ];

  if (compact) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-testid="path-cards-compact">
        {paths.map((path) => (
          <Card 
            key={path.id}
            className="flex-shrink-0 w-40 cursor-pointer hover:border-primary/50 transition-all"
            onClick={() => onSelect && onSelect(path.id)}
            data-testid={`path-card-${path.id}`}
          >
            <CardContent className="p-3 text-center">
              <div className={`w-10 h-10 rounded-full ${path.color} mx-auto mb-2 flex items-center justify-center`}>
                <path.icon className="h-5 w-5 text-white" />
              </div>
              <p className="font-medium text-sm">{path.name}</p>
              <p className="text-xs text-muted-foreground">{path.time}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="path-cards">
      {paths.map((path) => (
        <Card 
          key={path.id}
          className="cursor-pointer hover:border-primary/50 hover:shadow-lg transition-all group"
          onClick={() => onSelect && onSelect(path.id)}
          data-testid={`path-card-${path.id}`}
        >
          <CardContent className="p-4 text-center">
            <div className={`w-14 h-14 rounded-full ${path.color} mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform`}>
              <path.icon className="h-7 w-7 text-white" />
            </div>
            <p className="font-medium">{path.name}</p>
            <p className="text-xs text-muted-foreground mt-1">{path.description}</p>
            <div className="flex items-center justify-center gap-2 mt-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{path.time}</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Named exports for direct imports
export { PATH_DETAILS, PATH_RECOMMENDATIONS, GOALS };

export default {
  PreSignupTeaser,
  OnboardingModal,
  PathCards,
  GOALS,
  PATH_DETAILS,
  PATH_RECOMMENDATIONS
};
